import './as-help-hover.ts';
import { updateInputHelpHover } from './input-help-hover-utils.ts';
import type { SetValueOptions } from './types';
import { resolveSetValueSilence, upgradeProperty } from './utils';

const DEFAULT_LABEL = 'String';

type InternalSetValueOptions = {
  emitInput?: boolean;
  emitChange?: boolean;
  reflectAttr?: boolean;
  commit?: boolean;
};

export class AsStringInput extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['value', 'label-default-text', 'label-dict-key', 'placeholder', 'disabled', 'help-tooltip-default-text', 'help-tooltip-dict-key', 'help-placement', 'paramate-paramName', 'paramate-paramname'];
  }

  private _inputEl: HTMLInputElement | null = null;
  private _labelTextEl: HTMLElement | null = null;
  private _helpHoverEl: HTMLElement | null = null;
  private _value = '';
  private _lastCommittedValue = '';
  private _isReflectingValueAttr = false;
  private _hasRendered = false;

  private _paramName: string = '';
  public get paramName(): string {
    return this._paramName;
  }
  public set paramName(val: string) {
    this._paramName = val;
    if (val) {
      this._inputEl?.setAttribute('data-paramate-paramname', val);
      this._inputEl?.classList.add('paramateParam');
    }
    else {
      if (this._inputEl?.hasAttribute('data-paramate-paramname')) {
        this._inputEl?.removeAttribute('data-paramate-paramname');
      }
      if (this._inputEl?.classList.contains('paramateParam')) {
        this._inputEl?.classList.remove('paramateParam');
      }
    }
  }

  constructor() {
    super();
    this._handleInput = this._handleInput.bind(this);
    this._handleBlur = this._handleBlur.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
    this._handleNativeChange = this._handleNativeChange.bind(this);
  }

  connectedCallback(): void {
    if (!this._hasRendered) {
      this._render();
      this._hasRendered = true;
    }

    upgradeProperty(this, 'value');

    if (this.hasAttribute('value')) {
      this._setValueInternal(this.getAttribute('value') ?? '', {
        emitInput: false,
        emitChange: false,
        reflectAttr: false,
        commit: true,
      });
    } else if (this._inputEl) {
      this._inputEl.value = this._value;
    }

    if (this.hasAttribute('paramate-paramName')) {
      this.paramName = this.getAttribute('paramate-paramName') ?? '';
    }
    if (this.hasAttribute('paramate-paramname')) {
      this.paramName = this.getAttribute('paramate-paramname') ?? '';
    }

    this._updateLabel();
    this._updatePlaceholder();
    this._applyDisabledState();

    this._inputEl?.addEventListener('input', this._handleInput);
    this._inputEl?.addEventListener('blur', this._handleBlur);
    this._inputEl?.addEventListener('keydown', this._handleKeyDown);
    this._inputEl?.addEventListener('change', this._handleNativeChange);
  }

  disconnectedCallback(): void {
    this._inputEl?.removeEventListener('input', this._handleInput);
    this._inputEl?.removeEventListener('blur', this._handleBlur);
    this._inputEl?.removeEventListener('keydown', this._handleKeyDown);
    this._inputEl?.removeEventListener('change', this._handleNativeChange);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return;
    }

    switch (name) {
      case 'value':
        if (this._isReflectingValueAttr) {
          return;
        }
        this._setValueInternal(newValue ?? '', {
          emitInput: false,
          emitChange: true,
          reflectAttr: false,
          commit: true,
        });
        break;
      case 'label-default-text':
      case 'label-dict-key':
        this._updateLabel();
        break;
      case 'placeholder':
        this._updatePlaceholder();
        break;
      case 'disabled':
        this._applyDisabledState();
        break;
      case 'help-tooltip-default-text':
      case 'help-tooltip-dict-key':
      case 'help-placement':
        this._updateHelpHover();
        break;
      case 'paramate-paramName':
      case 'paramate-paramname':
        this.paramName = newValue ?? '';
        break;
    }
  }

  get value(): string {
    return this._value;
  }

  set value(val: string) {
    this.setValue(val);
  }

  public setValue(val: string, opt?: SetValueOptions): void {
    const { silenceInput, silenceChange } = resolveSetValueSilence(opt);

    this._setValueInternal(val ?? '', {
      emitInput: !silenceInput,
      emitChange: !silenceChange,
      reflectAttr: true,
      commit: true,
    });
  }

  private _render(): void {
    // CRITICAL: Save hovercard BEFORE wiping innerHTML (which destroys all children)
    const savedHovercard = this.querySelector('as-hovercard');

    this.innerHTML = /* html */ `
      <label class="col">
        <div style="display:flex;align-items:center;gap:4px;">
          <as-text class="label-text">${this.getAttribute('label-default-text') ?? DEFAULT_LABEL}</as-text>
          <span class="help-hover-container"></span>
        </div>
        <input type="text" />
      </label>
    `;

    this._labelTextEl = this.querySelector('.label-text');
    this._helpHoverEl = this.querySelector('.help-hover-container');
    this._inputEl = this.querySelector('input');

    // Pass saved hovercard since it was destroyed by innerHTML
    this._updateHelpHover(savedHovercard);
  }

  private _handleInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (!target) return;

    this._setValueInternal(target.value, {
      emitInput: true,
      emitChange: false,
      reflectAttr: true,
      commit: false,
    });
  }

  private _handleBlur(): void {
    this._emitChangeIfNeeded();
  }

  private _handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this._emitChangeIfNeeded();
    }
  }

  private _handleNativeChange(): void {
    this._emitChangeIfNeeded();
  }

  private _emitChangeIfNeeded(): void {
    if (!this._inputEl) {
      return;
    }
    const current = this._inputEl.value;
    if (current === this._lastCommittedValue) {
      return;
    }

    const previous = this._lastCommittedValue;
    this._emitChange(current, previous);
    this._lastCommittedValue = current;
  }

  private _setValueInternal(value: string, options: InternalSetValueOptions): void {
    const {
      emitInput = false,
      emitChange = false,
      reflectAttr = false,
      commit = false,
    } = options;
    const previous = this._value;
    const previousCommitted = this._lastCommittedValue;

    this._value = value;

    if (this._inputEl && this._inputEl.value !== value) {
      this._inputEl.value = value;
    }

    if (reflectAttr) {
      this._isReflectingValueAttr = true;
      try {
        if (value === '') {
          this.removeAttribute('value');
        } else {
          this.setAttribute('value', value);
        }
      } finally {
        this._isReflectingValueAttr = false;
      }
    }

    const shouldEmitInput = emitInput && previous !== value;
    const shouldEmitChange =
      emitChange && (previous !== value || previousCommitted !== value);

    if (shouldEmitInput) {
      this._emitInput(value, previous);
    }

    if (shouldEmitChange) {
      this._emitChange(value, previousCommitted);
    }

    if ((commit || shouldEmitChange) && this._lastCommittedValue !== value) {
      this._lastCommittedValue = value;
    }
  }

  private _emitInput(value: string, previous: string): void {
    const detail = {
      type: 'string' as const,
      value,
      previousValue: previous,
      phase: 'input' as const,
    };

    this.dispatchEvent(
      new CustomEvent('as-input-input', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  private _emitChange(value: string, previous: string): void {
    const detail = {
      type: 'string' as const,
      value,
      previousValue: previous,
      phase: 'change' as const,
    };

    this.dispatchEvent(
      new CustomEvent('as-input-change', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
    this.dispatchEvent(new CustomEvent('change', { detail: { value } }));
  }

  private _updateLabel(): void {
    if (!this._labelTextEl) {
      return;
    }
    const dictKey = this.getAttribute('label-dict-key');
    const defaultText = this.getAttribute('label-default-text') ?? DEFAULT_LABEL;

    if (dictKey) {
      this._labelTextEl.setAttribute('dict-key', dictKey);
    } else {
      this._labelTextEl.removeAttribute('dict-key');
    }

    this._labelTextEl.textContent = defaultText;
  }

  private _updatePlaceholder(): void {
    if (!this._inputEl) {
      return;
    }
    const placeholder = this.getAttribute('placeholder') ?? '';
    this._inputEl.placeholder = placeholder;
  }

  private _applyDisabledState(): void {
    if (!this._inputEl) {
      return;
    }
    const isDisabled = this.hasAttribute('disabled');
    this._inputEl.disabled = isDisabled;
  }

  private _updateHelpHover(savedHovercard?: Element | null): void {
    updateInputHelpHover(this, this._helpHoverEl, savedHovercard);
  }
}

customElements.define('as-string-input', AsStringInput);
